'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { PartnerReview } from '@/content/reviews';
import styles from './ReviewCards.module.css';

interface PartnerReviewCardProps {
  review: PartnerReview;
  currentLocale: 'en' | 'nl' | 'de';
  translations: {
    readMore: string;
    showLess: string;
    viewIntegration: string;
  };
  isExpanded?: boolean;
  onToggle?: () => void;
}

// Truncate text to approximately 300 characters
const truncateText = (text: string, maxLength: number = 300): { truncated: string; isTruncated: boolean } => {
  if (text.length <= maxLength) {
    return { truncated: text, isTruncated: false };
  }
  
  // Find the last space before maxLength
  const lastSpace = text.lastIndexOf(' ', maxLength);
  const truncateAt = lastSpace > 0 ? lastSpace : maxLength;
  
  return {
    truncated: text.substring(0, truncateAt) + '...',
    isTruncated: true,
  };
};

export function PartnerReviewCard({ review, currentLocale, translations, isExpanded: externalIsExpanded, onToggle }: PartnerReviewCardProps) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;
  const handleToggle = onToggle || (() => setInternalIsExpanded(!internalIsExpanded));
  
  // Get review text based on current locale, fallback to NL
  const reviewText = review[`review_${currentLocale}` as keyof PartnerReview] as string | undefined || review.review_nl;
  
  const { truncated, isTruncated } = truncateText(reviewText);
  const displayText = isExpanded ? reviewText : truncated;

  return (
    <div className={styles.partnerCard}>
      <div className={styles.partnerHeader}>
        {review.logo ? (
          <div className={styles.partnerLogo}>
            <Image
              src={review.logo}
              alt={`${review.company} logo`}
              width={60}
              height={60}
              className={styles.logoImage}
            />
          </div>
        ) : (
          <div className={styles.partnerLogoPlaceholder}>
            {review.company.charAt(0)}
          </div>
        )}
        <div className={styles.partnerInfo}>
          <div className={styles.partnerCompany}>{review.company}</div>
          {review.person && (
            <div className={styles.partnerPerson}>
              {review.person}
              {review.role && ` — ${review.role}`}
            </div>
          )}
        </div>
      </div>

      <div className={styles.partnerReviewText}>
        <p>{displayText}</p>
      </div>

      {isTruncated && (
        <button
          className={styles.expandButton}
          onClick={handleToggle}
          aria-expanded={isExpanded}
        >
          {isExpanded ? translations.showLess : translations.readMore}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={isExpanded ? styles.chevronUp : styles.chevronDown}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}

      {review.integrationPage && isExpanded && (
        <Link href={review.integrationPage} className={styles.integrationLink} prefetch={true}>
          {translations.viewIntegration}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </div>
  );
}

